/**
 * Platform abstraction (SPEC §1A.2, §5.2). Shared UI never imports a shell API directly; it goes
 * through this interface. `WebPlatform` is the only implementation until Phase N adds Tauri and
 * Capacitor. Every capability is feature-detected with a graceful fallback.
 */

export type NotifyOptions = {
  title: string;
  body?: string;
  tag?: string;
  /** Web Push notification actions where supported (e.g. "Flatten" opens a confirm screen). */
  actions?: Array<{ action: string; title: string }>;
};

export type HapticKind = "light" | "double" | "heavy";

export interface Platform {
  readonly kind: "web" | "tauri" | "capacitor";
  readonly isMobile: boolean;
  /** true when running as an installed app (display-mode: standalone). */
  readonly isInstalled: boolean;
  notify(opts: NotifyOptions): Promise<void>;
  /** Open a pop-out window (dockview popout). Requires a user gesture in browsers. */
  openWindow(url: string, name: string, features?: string): Window | null;
  /** Per-device secure-ish storage. On the web this is localStorage; native shells use the keychain. */
  secureStore: {
    get(key: string): Promise<string | null>;
    set(key: string, v: string): Promise<void>;
    delete(key: string): Promise<void>;
  };
  /** Passkey re-auth (WebAuthn userVerification=required). Never gates Flatten/Cancel/kill switch. */
  biometricUnlock(reason: string): Promise<boolean>;
  haptics(kind: HapticKind): void;
  /** The app came back (visible after hidden, bfcache restore, network back); see `watchResume`. */
  onResume(cb: () => void): () => void;
  setBadge(count: number): Promise<void>;
  /** Keep the screen awake while positions are open. Best-effort. */
  requestWakeLock(): Promise<() => void>;
}

function hasWindow(): boolean {
  return typeof window !== "undefined";
}

/** What `watchResume` needs of `document`. */
export type ResumeDocument = EventTarget & { readonly visibilityState: DocumentVisibilityState };

/**
 * Call `cb` whenever the app comes back and its sockets may be stale (CB4): the page becomes
 * visible after being hidden (phone unlocked, app switched back, tab re-selected), it is restored
 * from the back/forward cache (`pageshow` with `persisted`), or the network comes back (`online`,
 * e.g. airplane mode off). The callback checks liveness and resyncs; it must be cheap and
 * idempotent, since several of these can fire together. Returns the unsubscribe.
 */
export function watchResume(doc: ResumeDocument, win: EventTarget | null, cb: () => void): () => void {
  let hidden = doc.visibilityState === "hidden";
  const onVisibility = (): void => {
    if (doc.visibilityState === "hidden") {
      hidden = true;
      return;
    }
    if (!hidden) return;
    hidden = false;
    cb();
  };
  const onPageShow = (ev: Event): void => {
    if ((ev as Event & { persisted?: boolean }).persisted === true) cb();
  };
  const onOnline = (): void => {
    cb();
  };
  doc.addEventListener("visibilitychange", onVisibility);
  win?.addEventListener("pageshow", onPageShow);
  win?.addEventListener("online", onOnline);
  return () => {
    doc.removeEventListener("visibilitychange", onVisibility);
    win?.removeEventListener("pageshow", onPageShow);
    win?.removeEventListener("online", onOnline);
  };
}

export class WebPlatform implements Platform {
  readonly kind = "web" as const;

  get isMobile(): boolean {
    return hasWindow() && window.matchMedia("(max-width: 767px)").matches;
  }

  get isInstalled(): boolean {
    if (!hasWindow()) return false;
    const nav = window.navigator as Navigator & { standalone?: boolean };
    return window.matchMedia("(display-mode: standalone)").matches || nav.standalone === true;
  }

  async notify(opts: NotifyOptions): Promise<void> {
    if (!hasWindow() || !("Notification" in window)) return;
    if (Notification.permission !== "granted") return;
    const reg = await navigator.serviceWorker.getRegistration();
    const options: NotificationOptions & { actions?: NotifyOptions["actions"] } = {
      ...(opts.body !== undefined ? { body: opts.body } : {}),
      ...(opts.tag !== undefined ? { tag: opts.tag } : {}),
      ...(opts.actions !== undefined ? { actions: opts.actions } : {}),
    };
    if (reg) await reg.showNotification(opts.title, options);
    else new Notification(opts.title, options);
  }

  openWindow(url: string, name: string, features?: string): Window | null {
    if (!hasWindow()) return null;
    return window.open(url, name, features);
  }

  secureStore = {
    get: (key: string): Promise<string | null> => {
      try {
        return Promise.resolve(localStorage.getItem(`helm:${key}`));
      } catch {
        return Promise.resolve(null);
      }
    },
    set: (key: string, v: string): Promise<void> => {
      try {
        localStorage.setItem(`helm:${key}`, v);
      } catch {
        /* private mode / quota: best effort */
      }
      return Promise.resolve();
    },
    delete: (key: string): Promise<void> => {
      try {
        localStorage.removeItem(`helm:${key}`);
      } catch {
        /* ignore */
      }
      return Promise.resolve();
    },
  };

  async biometricUnlock(_reason: string): Promise<boolean> {
    // Wired to the passkey assertion flow in Phase 0.4 (auth). Until then, no gate.
    return Promise.resolve(true);
  }

  haptics(kind: HapticKind): void {
    if (!hasWindow() || typeof navigator.vibrate !== "function") return; // iOS: silently skipped
    const pattern = kind === "light" ? [15] : kind === "double" ? [15, 60, 15] : [60];
    navigator.vibrate(pattern);
  }

  onResume(cb: () => void): () => void {
    if (!hasWindow()) return () => {};
    return watchResume(document, window, cb);
  }

  async setBadge(count: number): Promise<void> {
    // Badging API: Chromium desktop and installed iOS apps (SPEC §1A.2). Feature-detected.
    if (!hasWindow() || !("setAppBadge" in navigator)) return;
    if (count > 0) await navigator.setAppBadge(count);
    else await navigator.clearAppBadge();
  }

  async requestWakeLock(): Promise<() => void> {
    if (!hasWindow() || !("wakeLock" in navigator)) return () => {};
    try {
      const lock = await navigator.wakeLock.request("screen");
      return () => {
        void lock.release();
      };
    } catch {
      return () => {};
    }
  }
}
